import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { computeScores } from '../lib/oura';
import { DailyMetrics } from '../types';
import PhenotypeMetricPanel from '../components/PhenotypeMetricPanel';
import RelapseWarning from '../components/RelapseWarning';
import PillarGrid from '../components/PillarGrid';
import PillarModal from '../components/PillarModal';
import { PILLAR_DEFS, computePillarScores } from '../lib/pillarScoring';
import InsightsPanel from '../components/InsightsPanel';
import { generateInsights } from '../lib/insights';
import { getWeek, weekToDate, localDateString } from '../lib/dashboardHelpers';
import { computeFocusAreas } from '../lib/focusAreas';
import useDashboardData from '../hooks/useDashboardData';
import SectionGroup from '../components/dashboard/SectionGroup';
import TopFocusAreas from '../components/TopFocusAreas';
import DashboardAppleWatch from '../components/DashboardAppleWatch';
import DashboardMentalHealth from '../components/DashboardMentalHealth';
import DashboardKeyLabs from '../components/DashboardKeyLabs';
import DashboardTrends from '../components/DashboardTrends';
import { syncPillarScores } from '../lib/pillarScoreSync';
import { useDemo } from '../lib/demoContext';
import { detectExceptions } from '../lib/exceptionDetector';
import { gradePhenotypes, PhenotypeInput } from '../lib/phenotype';
import { buildDashboardVM } from '../lib/dashboard/buildDashboardVM';
import TodaysPrimaryInsight from '../components/dashboard/TodaysPrimaryInsight';
import SystemOverview from '../components/dashboard/SystemOverview';
import PatternInterpretation from '../components/dashboard/PatternInterpretation';
import WeeklyDigest from '../components/WeeklyDigest';
import DashboardMetabolicCard from '../components/DashboardMetabolicCard';

type Range = 7 | 14 | 30;

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Average non-null numeric values, returning null if none exist. */
function avgMetric(vals: (number | null | undefined)[]): number | null {
  const v = vals.filter((x): x is number => x != null);
  return v.length > 0 ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
}

/** Build a single averaged DailyMetrics row from a list of daily rows. */
function averageDays(days: DailyMetrics[]): DailyMetrics {
  return {
    date:             days[days.length - 1].date,
    sleep_score:      avgMetric(days.map(d => d.sleep_score)),
    hrv:              avgMetric(days.map(d => d.hrv)),
    readiness:        avgMetric(days.map(d => d.readiness)),
    steps:            avgMetric(days.map(d => d.steps)),
    exercise_mins:    avgMetric(days.map(d => d.exercise_mins)),
    stress_score:     avgMetric(days.map(d => d.stress_score)),
    deep_sleep_mins:  avgMetric(days.map(d => d.deep_sleep_mins)),
    rem_sleep_mins:   avgMetric(days.map(d => d.rem_sleep_mins)),
    light_sleep_mins: avgMetric(days.map(d => d.light_sleep_mins)),
    total_sleep_mins: avgMetric(days.map(d => d.total_sleep_mins)),
    sleep_efficiency: avgMetric(days.map(d => d.sleep_efficiency)),
    lowest_hr:        avgMetric(days.map(d => d.lowest_hr)),
    bedtime_start:    days[days.length - 1].bedtime_start,
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const isDemo = useDemo();
  const [range, setRange] = useState<Range>(7);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [showTrends, setShowTrends] = useState(false);

  const [syncFailed, setSyncFailed] = useState(false);
  const handlePillarClick = useCallback((key: string) => setActiveModal(key), []);
  const handleToggleTrends = useCallback(() => setShowTrends(v => !v), []);
  const handleCloseModal = useCallback(() => setActiveModal(null), []);

  const {
    metrics, loading, error, appleExtras, appleDailyMaps, keyLabs,
    phq4Data, phq4Entries, phenoPrimary, phenoPrimaryResult,
    phq4ScreeningLevel, weeklyCheckins, homeLeaving, userAge, userSex, refresh,
  } = useDashboardData();

  // Track whether the dashboard has ever successfully loaded. Phase 7
  // absorbed DashboardSyncStatus into SystemOverview as an inline refresh
  // button, so clicking Refresh now re-triggers `loading=true` in the hook.
  // Without this gate, every refresh would unmount the entire content and
  // show the full-page spinner, hiding the inline "Refreshing…" state and
  // jarring the user. We only show the full-page spinner on the first
  // mount; subsequent refreshes keep the dashboard rendered so the inline
  // refresh button's spinning ↺ icon is visible.
  const hasEverLoaded = useRef(false);
  if (!loading) hasEverLoaded.current = true;
  const showInitialSpinner = loading && !hasEverLoaded.current;

  const recentMetrics = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - range);
    return metrics.filter(d => d.date >= localDateString(cutoff));
  }, [metrics, range]);

  // Always use yesterday (last complete day, not today) for rings + cards
  const yesterday = useMemo(() => {
    const today = localDateString();
    const complete = metrics.filter(d => d.date < today);
    return complete.length > 0 ? complete[complete.length - 1] : null;
  }, [metrics]);

  // Oura's daily_hrv endpoint lags 1-2 days — find most recent day with HRV data
  const latestHrvMetric = useMemo(() => {
    const today = localDateString();
    const withHrv = metrics.filter(d => d.date < today && d.hrv != null && (d.hrv as number) > 0);
    return withHrv.length > 0 ? withHrv[withHrv.length - 1] : null;
  }, [metrics]);

  // Sleep data may lag — find most recent day with actual sleep data
  // (yesterday might be an Apple-Health-only row with no Oura sleep)
  const latestSleepMetric = useMemo(() => {
    const today = localDateString();
    const withSleep = metrics.filter(d =>
      d.date < today && (
        (d.sleep_score != null && d.sleep_score > 0) ||
        (d.total_sleep_mins != null && d.total_sleep_mins > 0)
      )
    );
    return withSleep.length > 0 ? withSleep[withSleep.length - 1] : null;
  }, [metrics]);

  const latestCheckin = useMemo(() => {
    return weeklyCheckins.length > 0 ? weeklyCheckins[0] : null;
  }, [weeklyCheckins]);

  // Shared bundle of non-wearable inputs (labs, check-ins, apple extras)
  // These are point-in-time values that don't vary by period.
  const commonBundle = useMemo(() => {
    const symptoms = latestCheckin?.symptoms ? latestCheckin.symptoms.split(', ').filter(Boolean) : [];
    return {
      latestSleep: latestSleepMetric,
      latestHrv: latestHrvMetric?.hrv ?? null,
      latestCheckin,
      keyLabs,
      weightDelta: appleExtras.weightDelta,
      symptoms,
      restingHR: appleExtras.restingHR,
      homeLeaving,
      sleepHistory: metrics.filter(m => m.bedtime_start && m.total_sleep_mins && m.total_sleep_mins > 0),
      userAge,
      userSex,
      vo2Max: appleExtras.vo2Max,
      vo2MaxDelta: appleExtras.vo2MaxDelta,
      bmi: appleExtras.bmi,
      bodyFat: appleExtras.bodyFat,
      bodyFatDelta: appleExtras.bodyFatDelta,
    };
  }, [latestSleepMetric, latestHrvMetric, latestCheckin, keyLabs, appleExtras, homeLeaving, metrics]);

  // Score rings show yesterday's single-day values
  const scores = useMemo(() => {
    if (!yesterday) return computeScores(recentMetrics);
    const d = yesterday;
    const clampScore = (v: number) => Math.min(100, Math.max(0, Math.round(v)));
    const sleepSrc  = (d.sleep_score != null && d.sleep_score > 0) ? d : latestSleepMetric;
    const sleep     = sleepSrc?.sleep_score  ?? 0;
    const readiness = d.readiness ?? sleepSrc?.readiness ?? 0;
    const stress    = d.stress_score ?? 0;
    const hrvVal = latestHrvMetric?.hrv ?? d.hrv;
    const hrv = hrvVal && hrvVal > 0
      ? clampScore(((hrvVal - 15) / 105) * 100)
      : 0;
    const movement = (() => {
      if (d.steps == null && d.exercise_mins == null) return 0;
      const stepsPart = d.steps != null ? Math.sqrt(Math.min(1, d.steps / 10000)) * 50 : 0;
      const exPart    = d.exercise_mins != null ? Math.min(1, d.exercise_mins / 30) * 50 : 0;
      return clampScore(stepsPart + exPart);
    })();
    const vals = [sleep, hrv, readiness, movement, stress].filter(v => v > 0);
    const overall = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
    return { sleep, hrv, readiness, movement, stress, overall };
  }, [yesterday, recentMetrics, latestHrvMetric, latestSleepMetric]);

  // 30-day personal baseline (mean + SD)
  const baselineStats = useMemo(() => {
    function stat(vals: (number | null | undefined)[]) {
      const v = vals.filter((x): x is number => x != null && !isNaN(x) && x > 0);
      if (v.length < 7) return null;
      const mean = v.reduce((a, b) => a + b, 0) / v.length;
      const sd = Math.sqrt(v.reduce((a, b) => a + (b - mean) ** 2, 0) / v.length);
      return sd > 0.5 ? { mean, sd } : null;
    }
    const movementScores = metrics.map(d => {
      if (d.steps == null && d.exercise_mins == null) return null;
      const sp = d.steps != null ? Math.sqrt(Math.min(1, d.steps / 10000)) * 50 : 0;
      const ep = d.exercise_mins != null ? Math.min(1, d.exercise_mins / 30) * 50 : 0;
      const s = sp + ep;
      return s > 0 ? Math.min(100, Math.max(0, Math.round(s))) : null;
    });
    return {
      sleep:     stat(metrics.map(d => d.sleep_score)),
      hrv:       stat(metrics.map(d => d.hrv)),
      readiness: stat(metrics.map(d => d.readiness)),
      movement:  stat(movementScores),
      stress:    stat(metrics.map(d => d.stress_score)),
    };
  }, [metrics]);

  const latest = yesterday;

  const insights = useMemo(
    () => generateInsights({ metrics, phq4Entries, keyLabs }),
    [metrics, phq4Entries, keyLabs],
  );

  const weeksBehind = useMemo(() => {
    if (phq4Entries.length === 0) return 0;
    const thisWeek = getWeek();
    return Math.round(
      (weekToDate(thisWeek).getTime() - weekToDate(phq4Entries[0].week).getTime()) / (7 * 86400000)
    );
  }, [phq4Entries]);

  // ── Unified 6-Pillar Scores ──
  // When range=1 (Prior Day), use yesterday's single-day snapshot.
  // When range>1, average the wearable-driven pillars over the period.
  const pillarResults = useMemo(() => {
    if (recentMetrics.length < 2) {
      return computePillarScores({ yesterday, ...commonBundle });
    }

    const today = localDateString();
    const periodDays = recentMetrics.filter(d => d.date < today);
    if (periodDays.length === 0) {
      return computePillarScores({ yesterday, ...commonBundle });
    }

    const result = computePillarScores({ yesterday: averageDays(periodDays), ...commonBundle });

    // Append "/day" to raw values and "(avg Xd)" to subMetrics so user knows the period
    const daysLabel = `avg ${periodDays.length}d`;
    for (const key of Object.keys(result)) {
      const r = result[key];
      if (r.subMetric) r.subMetric = `${r.subMetric} (${daysLabel})`;
      // Add "/day" to movement-related inputs so averaged values are clearly daily
      for (const input of r.inputs) {
        if (input.label === 'Steps') input.rawValue = input.rawValue.replace(/ steps$/, ' steps/day');
        if (input.label === 'Exercise') input.rawValue = input.rawValue.replace(/ min$/, ' min/day');
      }
    }
    return result;
  }, [yesterday, commonBundle, range, recentMetrics]);

  // ── Prior-period pillar scores (for trend deltas) ──
  // Compare current period vs equivalent prior period
  // e.g. 7d view: current = last 7 days, prior = 7 days before that
  const pillarDeltas = useMemo(() => {
    const priorEnd = new Date();
    priorEnd.setDate(priorEnd.getDate() - range);
    const priorStart = new Date();
    priorStart.setDate(priorStart.getDate() - range * 2);

    const priorDays = metrics.filter(d =>
      d.date >= localDateString(priorStart) && d.date < localDateString(priorEnd)
    );
    if (priorDays.length === 0) return {} as Record<string, number>;

    const priorResults = computePillarScores({
      yesterday: averageDays(priorDays),
      ...commonBundle,
    });

    const deltas: Record<string, number> = {};
    for (const key of Object.keys(pillarResults)) {
      const cur = pillarResults[key]?.score;
      const prev = priorResults[key]?.score;
      if (cur != null && prev != null) {
        deltas[key] = cur - prev;
      }
    }
    return deltas;
  }, [metrics, range, pillarResults, commonBundle]);

  // Persist single-day pillar scores to Supabase (mobile reads these)
  // Always use yesterday's single-day snapshot — never the multi-day average
  const singleDayPillar = useMemo(() => {
    if (!yesterday) return null;
    return computePillarScores({ yesterday, ...commonBundle });
  }, [yesterday, commonBundle]);

  const lastSyncedDate = useRef<string | null>(null);
  useEffect(() => {
    if (isDemo || !yesterday || !singleDayPillar || loading) return;
    // Only sync once per data date to avoid redundant writes
    if (lastSyncedDate.current === yesterday.date) return;
    lastSyncedDate.current = yesterday.date;
    const sleepHist = metrics.filter(m => m.bedtime_start && m.total_sleep_mins && m.total_sleep_mins > 0);
    syncPillarScores(singleDayPillar, yesterday.date, sleepHist).then(ok => setSyncFailed(!ok)).catch(err => console.error('[Dashboard] syncPillarScores error:', err));
    // `metrics` is intentionally included — the effect reads it to build
    // `sleepHist`. The `lastSyncedDate` guard above prevents re-firing
    // on metric changes that don't change `yesterday.date`, so this can
    // never cause an infinite loop or redundant writes.
  }, [isDemo, singleDayPillar, yesterday, loading, metrics]);

  // 7-day sparkline data per pillar (raw proxy metrics, not full pillar scores)
  const sparklineData = useMemo(() => {
    const today = localDateString();
    const last7 = metrics.filter(d => d.date < today).slice(-7);
    if (last7.length < 2) return {} as Record<string, number[]>;

    const clamp = (v: number) => Math.min(100, Math.max(0, Math.round(v)));
    return {
      sleep: last7.map(d => d.sleep_score ?? 0).map(clamp),
      movement: last7.map(d => {
        const sp = d.steps != null ? Math.sqrt(Math.min(1, d.steps / 10000)) * 50 : 0;
        const ep = d.exercise_mins != null ? Math.min(1, d.exercise_mins / 30) * 50 : 0;
        return clamp(sp + ep);
      }),
      nutrition: last7.map(() => pillarResults.nutrition?.score ?? 0), // static (lab-based)
      stress: last7.map(d => d.stress_score ?? 0).map(clamp),
      social: last7.map(() => pillarResults.social?.score ?? 0), // static (checkin-based)
      substance: last7.map(() => pillarResults.substance?.score ?? 0), // static
    };
  }, [metrics, pillarResults]);

  // Top 3 focus areas
  const focusAreas = useMemo(() => computeFocusAreas({
    yesterday, latestHrvMetric, latestCheckin, phq4ScreeningLevel, keyLabs, recentMetrics, homeLeaving,
  }), [yesterday, latestHrvMetric, latestCheckin, phq4ScreeningLevel, keyLabs, recentMetrics, homeLeaving]);

  // ── Dashboard View Model ──
  // Single source of truth for sections that consume the new typed VM layer.
  // Built via the pure transformer in src/lib/dashboard/.
  const exceptions = useMemo(
    () => detectExceptions({
      currentPillarScores: pillarResults,
      dailySummaries: metrics,
      labs: keyLabs,
      phq4History: phq4Entries,
    }),
    [pillarResults, metrics, keyLabs, phq4Entries],
  );

  // Build the PhenotypeInput from existing hook outputs (no extra DB queries
  // — useDashboardData already has everything we need). Fed into
  // gradePhenotypes() to produce the full profile that drives the new
  // Pattern Interpretation section.
  const phenotypeInput = useMemo<PhenotypeInput>(() => {
    const input: PhenotypeInput = {};

    // PHQ-4 (latest values for grading). Use `phq4Data` — the hook's
    // already-normalized "latest q1-q4" field — instead of
    // `phq4Entries[0]`. In prod the entries are ordered created_at DESC
    // so [0] is the newest, but in demo mode DEMO_PHQ4_ENTRIES is built
    // oldest-first, so [0] is the WORST score in the 8-week history.
    // `phq4Data` is consistently the latest in both demo + prod.
    if (phq4Data) {
      input.phq4 = {
        q1: phq4Data.q1,
        q2: phq4Data.q2,
        q3: phq4Data.q3,
        q4: phq4Data.q4,
      };
    }

    // Weekly check-in — pillars, symptoms, appetite, thought/behavior patterns
    const latestCheckin = weeklyCheckins[0];
    if (latestCheckin) {
      input.pillars = {
        diet:       latestCheckin.diet ?? -1,
        stress:     latestCheckin.stress ?? -1,
        substances: latestCheckin.substances ?? -1,
        social:     latestCheckin.social ?? -1,
      };
      if (latestCheckin.symptoms) {
        input.symptoms = latestCheckin.symptoms
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (latestCheckin.appetite != null) {
        input.appetite = latestCheckin.appetite;
      }
      const rumFilled  = latestCheckin.rum_q1  != null && latestCheckin.rum_q2  != null && latestCheckin.rum_q3  != null;
      const badsFilled = latestCheckin.bads_q1 != null && latestCheckin.bads_q2 != null && latestCheckin.bads_q3 != null;
      const hintFilled = latestCheckin.hint_q1 != null && latestCheckin.hint_q2 != null && latestCheckin.hint_q3 != null;
      if (rumFilled || badsFilled || hintFilled) {
        input.checkin = {
          rumScore:       rumFilled  ? (latestCheckin.rum_q1!  + latestCheckin.rum_q2!  + latestCheckin.rum_q3!)  : 0,
          badsAvoidance:  badsFilled ? latestCheckin.bads_q2!                                                     : 0,
          badsActivation: badsFilled ? (latestCheckin.bads_q1! + latestCheckin.bads_q3!)                         : 0,
          hintScore:      hintFilled ? (latestCheckin.hint_q1! + latestCheckin.hint_q2! + latestCheckin.hint_q3!) : 0,
        };
      }
    }

    // Weight trend (from Apple Health extras)
    if (appleExtras.weightDelta != null) {
      input.weightDelta = appleExtras.weightDelta;
    }

    // Labs — normalize marker names and hand the core 14 markers to the
    // phenotype engine. Each lookup checks BOTH the short canonical key
    // (e.g. "b12") AND the full patient-facing key (e.g. "vitaminb12")
    // because some users have labs named "Vitamin B12" while others have
    // "B12", and we want to catch both. Same pattern for "Vitamin D"
    // (vs "25-OH Vitamin D"), "hsCRP" (vs "CRP" or "hs-CRP"), etc.
    if (keyLabs.length > 0) {
      const labMap: Record<string, number> = {};
      for (const l of keyLabs) {
        const key = l.marker.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!(key in labMap)) labMap[key] = l.value;
      }
      input.labs = {
        hsCRP:         labMap['hscrp'] ?? labMap['crp'],
        insulin:       labMap['insulin'] ?? labMap['fastinginsulin'],
        glucose:       labMap['glucose'] ?? labMap['fastingglucose'],
        hba1c:         labMap['hba1c'] ?? labMap['a1c'] ?? labMap['hemoglobina1c'],
        cortisol:      labMap['cortisol'] ?? labMap['amcortisol'],
        testosterone:  labMap['testosterone'] ?? labMap['totaltestosterone'],
        vitaminD:      labMap['vitamind'] ?? labMap['25ohvitamind'] ?? labMap['25hydroxyvitamind'],
        tsh:           labMap['tsh'],
        freeT4:        labMap['freet4'] ?? labMap['ft4'],
        b12:           labMap['b12'] ?? labMap['vitaminb12'],
        folate:        labMap['folate'] ?? labMap['folicacid'],
        ferritin:      labMap['ferritin'],
        triglycerides: labMap['triglycerides'] ?? labMap['trig'],
        omega3Index:   labMap['omega3index'] ?? labMap['omega3'],
      };
    }

    // Wearables — 7-day averages from the metrics array
    const recent7 = recentMetrics.slice(-7);
    if (recent7.length > 0) {
      const avg = (vals: (number | null | undefined)[]): number | undefined => {
        const nums = vals.filter((v): v is number => v != null && isFinite(v) && v > 0);
        return nums.length > 0 ? Math.round(nums.reduce((a, b) => a + b, 0) / nums.length) : undefined;
      };
      input.wearables = {
        avgHRV:         avg(recent7.map((m) => m.hrv)),
        avgSleepScore:  avg(recent7.map((m) => m.sleep_score)),
        avgStressScore: avg(recent7.map((m) => m.stress_score)),
        avgReadiness:   avg(recent7.map((m) => m.readiness)),
      };
    }

    // Body composition (from Apple Health extras)
    if (appleExtras.bmi != null || appleExtras.bodyFat != null || appleExtras.vo2Max != null) {
      input.bodyComposition = {
        bmi:          appleExtras.bmi ?? undefined,
        bodyFat:      appleExtras.bodyFat ?? undefined,
        vo2Max:       appleExtras.vo2Max ?? undefined,
        bodyFatDelta: appleExtras.bodyFatDelta ?? undefined,
        vo2MaxDelta:  appleExtras.vo2MaxDelta ?? undefined,
      };
    }

    return input;
  }, [phq4Data, weeklyCheckins, appleExtras, keyLabs, recentMetrics]);

  const phenotypeProfile = useMemo(
    () => gradePhenotypes(phenotypeInput),
    [phenotypeInput],
  );

  const dashboardVM = useMemo(
    () => buildDashboardVM({
      metrics,
      pillarResults,
      pillarDeltas,
      sparklineData,
      phenotypeProfile,
      insights,
      exceptions,
    }),
    [metrics, pillarResults, pillarDeltas, sparklineData, phenotypeProfile, insights, exceptions],
  );

  return (
    <main className="flex flex-col gap-5 lg:gap-6 pb-8" data-phase="7">
      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-2xl px-4 py-3 text-sm">
          {error} — Showing demo data.
        </div>
      )}
      {syncFailed && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-2xl px-4 py-3 text-sm">
          Mobile sync failed — scores may not update on the app until next refresh.
        </div>
      )}

      {showInitialSpinner ? (
        <div className="flex justify-center items-center py-24">
          <div className="w-8 h-8 border-4 border-arete-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* ── Phase 7 above-the-fold hero trio ───────────────────────
              Three and only three primary sections render before any
              secondary content. Budget verified on 1440×900 desktop
              and 375×812 mobile. Card paddings tightened on the
              Primary Insight hero (p-6 from p-9) to stay in budget.
              DashboardSyncStatus is absorbed into SystemOverview as
              an inline refresh button — the old standalone sync row
              no longer exists. */}
          <TodaysPrimaryInsight insight={dashboardVM.primaryInsight} />
          <SystemOverview
            data={dashboardVM.systemOverview}
            onRefresh={refresh}
            refreshing={loading}
          >
            <PillarGrid bare defs={PILLAR_DEFS} results={pillarResults} deltas={pillarDeltas} onPillarClick={handlePillarClick} range={range} onRangeChange={setRange} sparklineData={sparklineData} />
          </SystemOverview>
          <PatternInterpretation data={dashboardVM.patternInterpretation} />

          {/* ── Secondary content ──────────────────────────────────────
              Everything below the fold. DashboardMentalHealth and
              RelapseWarning render inline because they can be
              safety-critical (PHQ-4 ≥ 9 crisis banner, relapse risk
              warnings). Everything else that is less time-sensitive
              is grouped under collapsible SectionGroups so the
              dashboard scrolls short and the reader chooses what to
              expand. */}
          <DashboardMentalHealth
            phq4Data={phq4Data}
            phq4ScreeningLevel={phq4ScreeningLevel}
            phenoPrimary={phenoPrimary}
            weeksBehind={weeksBehind}
            hasEntries={phq4Entries.length > 0}
            phq4Entries={phq4Entries}
          />

          <RelapseWarning metrics={metrics} phq4Entries={phq4Entries} homeLeaving={homeLeaving} />

          {/* Top Priority + Weekly Summary remain compact and visible
              because they are short and help the reader triage the
              rest of the page. */}
          <TopFocusAreas focusAreas={focusAreas} compact />
          <WeeklyDigest insights={insights} pillarResults={pillarResults} pillarDeltas={pillarDeltas} metrics={metrics} compact />

          {/* Biological — Key Labs + Metabolic, collapsed by default. Empty
              when the user has no labs AND no Apple body composition data. */}
          <SectionGroup
            title="Biological"
            subtitle="Labs, metabolic markers, and body composition"
            testId="section-group-biological"
            isEmpty={
              keyLabs.length === 0 &&
              appleExtras.bodyFat == null &&
              appleExtras.bmi == null &&
              appleExtras.vo2Max == null
            }
          >
            <DashboardKeyLabs keyLabs={keyLabs} />
            <DashboardMetabolicCard bodyFat={appleExtras.bodyFat} bmi={appleExtras.bmi} vo2Max={appleExtras.vo2Max} labs={keyLabs} sex={userSex} />
          </SectionGroup>

          {/* Device Data — Apple Watch, collapsed by default. Empty when no
              wearable signals are present at all. */}
          <SectionGroup
            title="Device Data"
            subtitle="Wearable and mobile signals"
            testId="section-group-device-data"
            isEmpty={
              !latest &&
              appleExtras.restingHR == null &&
              appleExtras.distance == null
            }
          >
            <DashboardAppleWatch appleExtras={appleExtras} appleDailyMaps={appleDailyMaps} latest={latest} homeLeaving={homeLeaving} range={range} onRangeChange={setRange} />
          </SectionGroup>

          <InsightsPanel insights={insights} />

          {phenoPrimaryResult && metrics.length > 0 && (
            <PhenotypeMetricPanel
              phenotype={phenoPrimaryResult}
              metrics={metrics}
              appleExtras={{ restingHR: appleExtras.restingHR, weight: appleExtras.weight }}
              compact
            />
          )}

          <DashboardTrends
            showTrends={showTrends}
            onToggle={handleToggleTrends}
            range={range}
            recentMetrics={recentMetrics}
            metrics={metrics}
          />
        </>
      )}

      {activeModal && (() => {
        const def = PILLAR_DEFS.find(p => p.key === activeModal);
        const result = pillarResults[activeModal];
        if (!def || !result) return null;
        const today = localDateString();
        const periodDays = recentMetrics.filter(d => d.date < today);
        const isAverage = periodDays.length >= 2;
        const periodLabel = isAverage
          ? `${periodDays.length}-Day Daily Average`
          : yesterday ? `${new Date(yesterday.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : undefined;
        return <PillarModal def={def} result={result} onClose={handleCloseModal} periodLabel={periodLabel} />;
      })()}
    </main>
  );
}
